import { m } from "framer-motion";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

/**
 * Shown in place of any create/edit form while the organisation's
 * subscription is expired, suspended or cancelled. The backend already
 * refuses those writes with a 402; showing the form anyway invited people
 * to fill it in and only learn at the last step that nothing could be
 * saved. A reader who can act on it (an admin) gets the way to the
 * subscription page; anyone else is told whom to ask.
 */
export function SubscriptionExpired({ reason }: { reason?: string | null }) {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = !!(user?.isAdmin || user?.isAppAdmin);
  const reasonText = (reason || "inactive").toLowerCase();

  return (
    <m.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex min-h-[400px] flex-col items-center justify-center rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-8 text-center shadow-lifted"
    >
      <div className="relative mb-6">
        <div className="absolute inset-0 rounded-full bg-red-500 opacity-10 blur-xl animate-pulse" />
        <div className="relative rounded-full border border-red-500/30 bg-red-500/10 p-5 text-red-600 dark:text-red-400">
          <Lock size={48} />
        </div>
      </div>

      <h2 className="mb-2 text-2xl font-bold text-[rgb(var(--text-primary))]">
        {t("subscriptionExpired.title", { defaultValue: "Subscription {{reason}}", reason: reasonText })}
      </h2>
      <p className="max-w-md leading-relaxed text-[rgb(var(--text-muted))]">
        {t("subscriptionExpired.description", {
          defaultValue:
            "Forms are disabled while your organisation's subscription is {{reason}}. Existing records stay readable; nothing new can be created or changed until it is renewed.",
          reason: reasonText,
        })}
      </p>

      {canManage ? (
        <button
          type="button"
          onClick={() => navigate("/admin/subscription")}
          className="mt-6 rounded-lg bg-[rgb(var(--accent-fill))] px-4 py-2 text-sm font-semibold text-[rgb(var(--accent-ink))] hover:opacity-90"
        >
          {t("subscriptionExpired.manage", { defaultValue: "Recharge / Upgrade" })}
        </button>
      ) : (
        <p className="mt-6 text-xs font-medium text-[rgb(var(--text-accent))]">
          {t("subscriptionExpired.askAdmin", {
            defaultValue: "Ask your organisation's admin to renew the subscription.",
          })}
        </p>
      )}
    </m.div>
  );
}
